import React, { useEffect, useState } from 'react';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { getStatusColor } from '../utils/requestUtils';

const RequestDetails = ({ request, handleStatusUpdate, isOutbound }) => {
  const [toolImage, setToolImage] = useState(null);
  const [confirmationMessage, setConfirmationMessage] = useState(null);

  useEffect(() => {
    const fetchToolImage = async () => {
      if (request && request.toolId) {
        const toolDoc = await getDoc(doc(db, 'tools', request.toolId));
        if (toolDoc.exists()) {
          const toolData = toolDoc.data();
          setToolImage(toolData.imageUrls?.[0] || null);
        }
      }
    };

    fetchToolImage();
  }, [request]);

  if (!request) {
    return (
      <div className="bg-white border rounded-lg shadow-sm p-6 min-h-[200px] flex items-center justify-center">
        <p className="text-gray-500 text-center">No request selected</p>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime())
      ? 'Invalid Date'
      : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleStatusUpdateClick = async (newStatus) => {
    try {
      await handleStatusUpdate(request.id, newStatus);
      setConfirmationMessage(`Request ${newStatus} successfully.`);
      setTimeout(() => setConfirmationMessage(null), 3000);
    } catch (error) {
      console.error("Error updating status:", error);
      setConfirmationMessage("Failed to update request status. Please try again.");
    }
  };

  return (
    <div className="bg-white border rounded-lg shadow-sm">
      {confirmationMessage && (
        <div className="p-3 bg-green-100 border-b border-green-200 text-green-700 text-sm">
          {confirmationMessage}
        </div>
      )}
      
      <div className="p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-start md:space-x-6">
          <div className="w-full md:w-24 h-48 md:h-24 mb-4 md:mb-0 rounded-lg overflow-hidden flex-shrink-0">
            {toolImage ? (
              <img 
                src={toolImage} 
                alt={request.toolName} 
                className="w-full h-full object-cover" 
              />
            ) : (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                <span className="text-gray-400 text-xs">No image</span>
              </div>
            )}
          </div>
          
          <div className="flex-grow min-w-0">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3">
              <h2 className="text-xl font-semibold text-gray-900 mb-2 md:mb-0">
                {request.toolName}
              </h2>
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(request.status)} mb-2 md:mb-0`}>
                {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
              </span>
            </div>

            {request.requestedDates && request.requestedDates.length > 0 && (
              <div className="flex items-center space-x-2 mb-3">
                <FontAwesomeIcon icon={faCalendar} className="text-gray-500" />
                <span className="text-gray-500 text-sm md:text-base">
                  {formatDate(request.requestedDates[0].startDate)} → {formatDate(request.requestedDates[0].endDate)}
                </span>
              </div>
            )}

            <div className="text-gray-500 mb-3">
              <span className="font-medium text-sm md:text-base">
                {isOutbound ? `Owner: ${request.ownerName}` : `Borrower: ${request.borrowerName}`}
              </span>
            </div>
          </div>
        </div>

        {request.status === 'pending' && (
          <div className="mt-4 md:mt-6 pt-4 border-t flex flex-col md:flex-row md:justify-end items-stretch md:items-center space-y-2 md:space-y-0 md:space-x-3">
            {isOutbound ? (
              <button
                onClick={() => handleStatusUpdateClick('cancelled')}
                className="w-full md:w-auto inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel Request
              </button>
            ) : (
              <>
                <button
                  onClick={() => handleStatusUpdateClick('declined')}
                  className="w-full md:w-auto inline-flex justify-center items-center px-4 py-2 border border-red-300 text-sm font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Decline
                </button>
                <button
                  onClick={() => handleStatusUpdateClick('approved')}
                  className="w-full md:w-auto inline-flex justify-center items-center px-4 py-2 text-sm font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Approve
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestDetails;
